import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Col, Row, Spin, Tooltip } from 'antd';
import { Block } from 'components/block';
import { InfoCircleIcon } from 'components/icons';
import { useProfile } from 'hooks';
import { CertificatesView } from 'src/domains/employee/certificates-view';
import { DomainsView } from 'src/domains/employee/domains-view';
import { InterestsView } from 'src/domains/employee/interests-view';
import { LanguageView } from 'src/domains/employee/languages-view';
import { PersonalInfoView } from 'src/domains/employee/personal-info-view';
import { PlatformsView } from 'src/domains/employee/platforms-view';
import { TechnologiesView } from 'src/domains/employee/technologies-view';
import styles from './styles.less';

export const ViewProfile: FC = () => {
  const { t } = useTranslation();
  const GUTTER = 16;
  const SPAN_FULL = 24;

  const { linkHash } = useParams();
  const { employee, loading } = useProfile(linkHash || '');

  if (loading || !employee) {
    return <Spin className={styles['spinner']} />;
  }

  const technologiesToShow = ['EP', 'PRIMARY_EMS', 'PRIMARY_EP'];

  const techs = employee.technologies.filter((tech) =>
    technologiesToShow.includes(tech.desc),
  );
  const platforms = employee.platforms.filter((plat) =>
    technologiesToShow.includes(plat.desc),
  );
  const domains = employee.domains.filter((domain) =>
    technologiesToShow.includes(domain.desc),
  );

  const technologiesTooltip = (
    <Tooltip
      overlayClassName={styles['tooltip']}
      title={
        'Primary technology is the main skill that possessed and used during your work. This technology is integrated with EMS.'
      }
    >
      <InfoCircleIcon />
    </Tooltip>
  );

  const platformsTooltip = (
    <Tooltip title="Platforms are the systems that provided a variety of solutions for optimizing the company's business processes.">
      <InfoCircleIcon />
    </Tooltip>
  );

  const domainsTooltip = (
    <Tooltip title="Domains are a sphere of activity where you have any experience.">
      <InfoCircleIcon />
    </Tooltip>
  );

  return (
    <Row gutter={[GUTTER, GUTTER]}>
      <Col span={SPAN_FULL}>
        <PersonalInfoView employee={employee} />
      </Col>
      <Col span={SPAN_FULL}>
        <Block title={t('Technologies')} subTitle={technologiesTooltip}>
          <TechnologiesView technologies={techs} />
        </Block>
      </Col>
      <Col span={SPAN_FULL}>
        <Block title={t('Platforms')} subTitle={platformsTooltip}>
          <PlatformsView platforms={platforms} />
        </Block>
      </Col>
      <Col span={SPAN_FULL}>
        <Block title={t('Domains')} subTitle={domainsTooltip}>
          <DomainsView domains={domains} />
        </Block>
      </Col>
      <Col span={SPAN_FULL}>
        <LanguageView
          englishLevel={employee.englishLevel}
          languages={employee.languages}
        />
      </Col>
      <Col span={SPAN_FULL}>
        <CertificatesView certificates={employee.certificates} />
      </Col>
      <Col span={SPAN_FULL}>
        <Block title={t('Interests')}>
          <InterestsView interests={employee.interests} />
        </Block>
      </Col>
    </Row>
  );
};
